
import React, { ReactNode, useState, useTransition, useCallback, useEffect, useRef } from 'react'
import { useDarkMode } from '@/components/canvas/providers/DarkModeProvider'
import { useStore } from '@xyflow/react'
import { Card } from '@/components/ui/card'
import { Icon } from '@/components/ui/icon'
import { MenuIcon } from '@/components/workspace/context-menu/MenuIcon'
import { Button } from '@/components/ui/button'
import { InlineRename } from '@/components/ui/inline-rename'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { NodeDebugPanel } from './NodeDebugPanel'
import { NODE_VISUAL_CONSTANTS, NODE_DIMENSIONS, NODE_LAYOUT, renderedNodeBox, cssLength } from '@genieartinc/schemas'
import type { IconName } from '@/components/ui/icon'
/** Scaffold colors per node type — dark-tinted variants of Transistor palette */
const SCAFFOLD_COLORS_DARK: Record<string, { hover: string; selected: string }> = {
  text:   { hover: '#1c2d4a', selected: '#1e3d6e' },   // blue    (#2979FF)
  image:  { hover: '#261c4a', selected: '#3b1e6e' },   // purple  (#7C4DFF)
  video:  { hover: '#2e1c1b', selected: '#4e2220' },   // red     (#F44336)
  '3d':   { hover: '#152c30', selected: '#0d4850' },   // teal    (#00BCD4)
  shader: { hover: '#2e1822', selected: '#4e1838' },   // pink    (#E91E63)
  plugin: { hover: '#2d2b18', selected: '#4a4518' },   // yellow  (#FFD600)
}
const DEFAULT_SCAFFOLD_DARK = { hover: '#1c2e1d', selected: '#1e4a22' } // green (#4CAF50)

/** Scaffold colors per node type — light-tinted pastel variants */
const SCAFFOLD_COLORS_LIGHT: Record<string, { hover: string; selected: string }> = {
  text:   { hover: '#e8f0fe', selected: '#d2e3fc' },
  image:  { hover: '#f0e8fe', selected: '#e1d2fc' },
  video:  { hover: '#fee8e7', selected: '#fcd2d0' },
  '3d':   { hover: '#e0f7fa', selected: '#b2ebf2' },
  shader: { hover: '#fce4ec', selected: '#f8bbd0' },
  plugin: { hover: '#fff9c4', selected: '#fff176' },
}
const DEFAULT_SCAFFOLD_LIGHT = { hover: '#e8f5e9', selected: '#c8e6c9' }

interface NodeContainerProps {
  nodeId?: string // React Flow node ID — its saved width is authoritative
  title: string
  icon: string | React.ReactNode
  selected?: boolean
  nodeType?: string // Node type for selection color (text, image, video, 3d, shader, plugin)
  width?: number
  onSelect?: () => void
  onHoverChange?: (hovered: boolean) => void
  onStateChange?: (updates: Record<string, unknown> | ((prev: Record<string, unknown>) => Record<string, unknown>)) => void
  onTitleRename?: (newTitle: string) => Promise<void> | void
  state?: Record<string, unknown>
  children: ReactNode
  cardRef?: React.RefObject<HTMLDivElement | null>
  borderClass?: string
  className?: string
}

export function NodeContainer({
  nodeId,
  title,
  icon,
  selected = false,
  nodeType,
  width = NODE_DIMENSIONS.TextNode.width,
  onSelect,
  onHoverChange,
  onStateChange,
  onTitleRename,
  state = {},
  children,
  cardRef,
  borderClass = 'border-border',
  className = ''
}: NodeContainerProps) {
  // Requested box, never the measured one: feeding measurements back into
  // layout creates resize loops. A missing height is the square side, not
  // content height — that content-hug is what made new nodes wide rectangles.
  const box = useStore((store) => {
    const node = nodeId ? store.nodeLookup.get(nodeId) : undefined
    const user = node?.internals.userNode
    return renderedNodeBox({
      styleWidth: cssLength(user?.style?.width),
      styleHeight: cssLength(user?.style?.height),
      liveWidth: node?.width,
      liveHeight: node?.height,
      resizing: Boolean(user?.resizing),
      fallbackWidth: width,
    })
  }, (a, b) => a.width === b.width && a.height === b.height)
  const [isEditingTitle, setIsEditingTitle] = useState(false)
  const [showDebug, setShowDebug] = useState(false)

  // ⚡ Performance: useTransition for non-urgent state updates (React 18)
  const [isPending, startTransition] = useTransition()

  // Use current title from state (no optimistic state needed with transition)
  const currentTitle = (state.title as string | undefined) ?? title

  const handleTitleSave = async (newTitle: string) => {
    // ⚡ Performance: Wrap in startTransition for smoother UX
    // UI updates immediately, state update is non-blocking
    startTransition(() => {
      if (onStateChange) {
        onStateChange((prev) => ({ ...prev, title: newTitle, titleSource: 'custom' }))
      }
    })

    // Call optional callback if provided
    if (onTitleRename) {
      await onTitleRename(newTitle)
    }
  }

  const handleTitleCancel = () => {
    setIsEditingTitle(false)
  }

  // F2 shortcut: rename selected node inline
  useEffect(() => {
    if (!selected) return
    const handler = () => setIsEditingTitle(true)
    document.addEventListener('genie:rename-selected', handler)
    return () => document.removeEventListener('genie:rename-selected', handler)
  }, [selected])

  const BORDER_RADIUS = NODE_VISUAL_CONSTANTS.borderRadius.card;

  /** Shared InlineRename props used in both collapsed and expanded views */
  const inlineRenameProps = {
    value: currentTitle,
    onSave: handleTitleSave,
    onCancel: handleTitleCancel,
    isEditing: isEditingTitle,
    onStartEdit: () => setIsEditingTitle(true),
    variant: 'invisible' as const,
    size: 'sm' as const,
    textClassName: 'text-sm font-medium text-foreground',
    inputClassName: 'text-sm font-medium text-foreground bg-muted rounded-md px-2 py-0.5',
    disableSaveOnBlur: false,
    autoFocus: true,
    enableDoubleClick: true,
    showSaveButton: false,
  }

  // Render icon: supports both string names (dynamic) and React elements (static)
  const renderIcon = (className: string) => {
    if (typeof icon === 'string') {
      return <Icon name={icon as IconName} className={className} />
    }
    return <span className={className}>{icon}</span>
  }

  // Dark mode detection via shared provider (single MutationObserver for all nodes)
  const isDark = useDarkMode()

  // Hover state for scaffold color
  const [isHovered, setIsHovered] = useState(false)
  const handleMouseEnter = useCallback(() => {
    setIsHovered(true)
    onHoverChange?.(true)
  }, [onHoverChange])

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false)
    onHoverChange?.(false)
  }, [onHoverChange])

  // Scaffold bg color: default → hover → selected
  const scaffoldColorMap = isDark ? SCAFFOLD_COLORS_DARK : SCAFFOLD_COLORS_LIGHT
  const defaultScaffold = isDark ? DEFAULT_SCAFFOLD_DARK : DEFAULT_SCAFFOLD_LIGHT
  const scaffoldColors = nodeType ? scaffoldColorMap[nodeType] || defaultScaffold : defaultScaffold
  const scaffoldBg = selected
    ? scaffoldColors.selected
    : isHovered
      ? scaffoldColors.hover
      : isDark ? '#171717' : '#e4e4e7'

  // Scaffold text color: dark icons on light scaffold, white icons on dark scaffold
  const scaffoldText = isDark ? '#ffffff' : '#27272a'

  // Propagate scaffold bg + text to React Flow node wrapper so handles can read it
  const outerRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const rfNode = outerRef.current?.closest('.react-flow__node') as HTMLElement | null
    if (rfNode) {
      rfNode.style.setProperty('--scaffold-bg', scaffoldBg)
      rfNode.style.setProperty('--scaffold-text', scaffoldText)
    }
  }, [scaffoldBg, scaffoldText])

  // Expanded version
  return (
    <div
      ref={outerRef}
      className="relative flex flex-col overflow-hidden"
      data-node-container
      style={{
        width: box.width,
        height: box.height,
        minWidth: `max(${NODE_LAYOUT.minWidth}px, var(--node-port-min-width, 0px))`,
        minHeight: 'var(--node-port-min-height, 0px)',
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Tab header row */}
      <div data-node-header className="relative z-10 flex shrink-0 items-center justify-between" style={{ height: NODE_LAYOUT.headerHeight }}>
        <div className="flex min-w-0 items-end">
          <div className="flex min-w-0 items-center gap-[4px] rounded-tl-[12px] rounded-tr-[12px] px-[9px] py-[4px] transition-colors duration-200" style={{ backgroundColor: scaffoldBg }}>
            {renderIcon("h-4 w-4 flex-shrink-0 text-foreground")}
            <InlineRename
              {...inlineRenameProps}
              className="min-w-0"
              textClassName="truncate font-mono text-[14px] font-medium leading-[20px] text-foreground uppercase"
              inputClassName="font-mono text-[14px] font-medium leading-[20px] text-foreground bg-black/10 dark:bg-white/10 rounded-md px-2 py-0.5 min-w-[120px]"
            />
          </div>
          {state.titleSource !== 'automatic' && typeof state.modelName === 'string' && (
            <span className="min-w-0 truncate px-2 text-xs text-muted-foreground" title={`Model: ${state.modelName}`}>Model: {state.modelName}</span>
          )}
          {/* Inverse corner radius connecting tab to card */}
          <svg width="8" height="8" viewBox="0 0 8 8" fill="none" className="flex-shrink-0">
            <path d="M0 0C0 4.418 3.582 8 8 8H0V0Z" fill={scaffoldBg} style={{ transition: 'fill 0.2s' }} />
          </svg>
        </div>
        {/* Ellipsis menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              aria-label="Node options"
              aria-haspopup="menu"
              className="mr-1 h-[16px] w-[16px] flex-shrink-0 rounded-[4px] p-0 text-muted-foreground hover:text-foreground"
              onClick={(e) => e.stopPropagation()}
            >
              <MenuIcon name="ellipsis" className="h-3.5 w-3.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => setIsEditingTitle(true)}>
              <MenuIcon name="edit" className="h-4 w-4 mr-2" />
              Rename
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => document.dispatchEvent(new CustomEvent('genie:duplicate-selected'))}>
              <MenuIcon name="copy" className="h-4 w-4 mr-2" />
              Duplicate
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setShowDebug(prev => !prev)}>
              <MenuIcon name="bug" className="h-4 w-4 mr-2" />
              {showDebug ? 'Hide Debug' : 'Debug'}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => document.dispatchEvent(new CustomEvent('genie:delete-selected'))}
              className="text-destructive"
            >
              <MenuIcon name="trash" className="h-4 w-4 mr-2" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Card body */}
      <Card
        ref={cardRef}
        data-node-hitbox
        className={`group relative flex min-h-0 flex-1 cursor-pointer flex-col overflow-hidden border-0 transition-colors duration-200 outline-none ${className}`}
        style={{
          width: '100%',
          minHeight: `max(0px, calc(var(--node-port-min-height, 0px) - ${NODE_LAYOUT.headerHeight}px))`,
          borderRadius: `0 ${BORDER_RADIUS}px ${BORDER_RADIUS}px ${BORDER_RADIUS}px`,
          backgroundColor: scaffoldBg,
        }}
        tabIndex={0}
        role="group"
        aria-label={`${currentTitle} node`}
        onClick={onSelect}
      >
        {/* Inner content area — darker, rounded corners */}
        <div className="m-1.5 flex min-h-0 min-w-0 flex-1 flex-col overflow-auto rounded-[10px] bg-card">
          {children}
          {showDebug && nodeId && (
            <NodeDebugPanel
              nodeId={nodeId}
              state={state}
              onClose={() => setShowDebug(false)}
            />
          )}
        </div>
      </Card>
    </div>
  )
}
